import React, { lazy, Suspense, useMemo } from "react";
import type { HelmetZone, HelmetZoneData } from "@components/HelmetHeatMap/HelmetHeatMap";
import type { HelmetLocation } from "./types";

// A normal import would add three.js to the bundle of the Team page.
const HelmetHeatMap = lazy(() =>
  import("@components/HelmetHeatMap/HelmetHeatMap").then((m) => ({ default: m.HelmetHeatMap })),
);

export const MAP_HEIGHT = 400;

export interface ZoneCount {
  location: HelmetLocation;
  zone: HelmetZone;
  count: number;
  share: number;
  color: string | null;
}

interface Props {
  zones: ZoneCount[];
  selectedDay: string | null;
  selectedLocations: HelmetLocation[];
  onToggleLocation: (location: HelmetLocation) => void;
  /** The PDF export: WebGL captures blank, so the panel shows the bird's-eye SVG. */
  exportPreview: boolean;
}

const percent = (share: number) => `${Math.round(share * 100)}%`;
const dayLabel = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString("en-US", {
    timeZone: "UTC",
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export function HelmetZonesPanel({ zones, selectedDay, selectedLocations, onToggleLocation, exportPreview }: Props) {
  const data = useMemo(() => {
    if (!selectedDay) return {};
    return Object.fromEntries(
      zones.map((z): [HelmetZone, HelmetZoneData] => [
        z.zone,
        { value: z.count, color: z.color ?? undefined, tooltip: `${z.location} · ${z.count} impacts (${percent(z.share)})` },
      ]),
    );
  }, [zones, selectedDay]);
  const selected = useMemo(
    () => zones.filter((z) => selectedLocations.includes(z.location)).map((z) => z.zone),
    [zones, selectedLocations],
  );

  return (
    <div className="helmet-map-cell">
      {selectedDay && <div className="helmet-map-day">{dayLabel(selectedDay)}</div>}
      <Suspense fallback={<div style={{ height: MAP_HEIGHT, width: MAP_HEIGHT }} />}>
        <HelmetHeatMap
          mode={exportPreview ? "svg" : "3d"}
          data={data}
          selected={selected}
          onZoneClick={(zone) => {
            const location = zones.find((z) => z.zone === zone)?.location;
            if (location) onToggleLocation(location);
          }}
          height={MAP_HEIGHT}
          autoRotate
        />
      </Suspense>
    </div>
  );
}
